/*
 * Copyright (c) 2023 Puratos. All rights reserved.
 */

import React from 'react';
import PropTypes from 'prop-types';
import {createSelector} from 'reselect';
import {FilterType} from "../../data/finders.model";
import {Filter} from "../organisms/finders/filter/Filter";
import FilterPanel from "../organisms/finders/panels/FilterPanel";
import {FlexRow} from "../atoms/layout/FlexRow";
import {Pagination} from "../molecules/Pagination";
import {createPaginationSelector} from "../../selectors/generic";
import {CardList} from "../organisms/finders/CardList";
import {Title} from "../atoms/text/Title";
import {TextColors} from "../../config/styles";
import Block from "../atoms/layout/Block";
import {ShowHide} from "../atoms/layout/ShowHide";
import Link from "../atoms/text/Link";
import {connect} from "react-redux";
import {closeFinderPanel, resetAllFilters, setPagination, setSorting} from "../../actions/finder";
import {filtersSelector, getPage, getSidepanel, getSorting, getTagFilters, numberOfActiveFilterSelector} from "../../selectors/finder";
import {Select} from "../atoms/forms/Select";
import {withI18N} from "../hoc/withI18N";
import {compose} from "redux";
import * as ReactDOM from "react-dom";
import {isLoggedIn} from "../../data/user.model";
import ResultCounter from "../my-puratos/atoms/list/ResultCounter";
import {WithSkeleton} from "../my-puratos/hoc/WithSkeleton";
import {fetchProductTileData} from "../../actions/products";
import {trackGA4FinderImpressions} from "../../analytics/ga4/events/impression";
import {getCurrencyCode} from "../../selectors/cart";
import {selectUserId} from "../../selectors/auth";

class _Finder extends React.Component {

    static defaultProps = {
        items: [],
        children: [],
        filters: [],
        pageSize: 12,
        showPricingOnCards: false
    };

    static propTypes = {
        resource: PropTypes.object,
        filters: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.shape(FilterType)), PropTypes.array]),
        panel: PropTypes.any,
        card: PropTypes.func,
        page: PropTypes.number,
        count: PropTypes.number,
        items: PropTypes.array,
        pageSize: PropTypes.number,
        paginatedResults: PropTypes.array,
        header: PropTypes.node,
        children: PropTypes.arrayOf(PropTypes.any),
        resetAllFilters: PropTypes.func,
        hasFiltering: PropTypes.bool,
        availableTags: PropTypes.arrayOf(PropTypes.string),
        ctaCard: PropTypes.node,
        sorting: PropTypes.string,
        sortingOptions: PropTypes.arrayOf(PropTypes.shape({
            label: PropTypes.string,
            value: PropTypes.string,
        })),
        setSorting: PropTypes.func,
        search: PropTypes.node,
        setSearch: PropTypes.func,
        setPagination: PropTypes.func,
        renderHeader: PropTypes.func,
        renderSubheader: PropTypes.func,
        renderFooter: PropTypes.func,
        paginationCallback: PropTypes.func,
        progressivePagination: PropTypes.bool,
        paginationAmount: PropTypes.number,
        showCtaNoResultsOnly: PropTypes.bool,
        showSearchItemAsFilter: PropTypes.bool,
        removeSearchItem: PropTypes.func,
        showPricingOnCards: PropTypes.bool
    };

    constructor(props) {
        super(props);

        this.state = {responsiveFiltersOpen: false};
        this.updatePagination = this.updatePagination.bind(this);
        this.props.setSorting(this.props.resource?.defaultSortingOption || "weight");
    }

    componentDidUpdate = (oldProps) => {
        const { page, showPricingOnCards, paginatedResults, fetchProductTileData, productTileData, userId, currencyCode } = this.props;

        if (page !== oldProps.page) {
            ReactDOM.findDOMNode(this).scrollIntoView({behavior: 'smooth'});
        }

        if (isLoggedIn() && showPricingOnCards && paginatedResults !== oldProps.paginatedResults && paginatedResults?.length > 0) {
            fetchProductTileData({ids: paginatedResults?.map(product => product.id)});
        }

        if ((((!isLoggedIn() || !showPricingOnCards) && paginatedResults !== oldProps.paginatedResults) ||
                (isLoggedIn() && showPricingOnCards && productTileData !== oldProps.productTileData)
            ) && paginatedResults?.length > 0) {
            trackGA4FinderImpressions(paginatedResults, productTileData, userId, currencyCode, this.props.store);
        }
    };

    toggleFilters = () => {
        let cb = undefined;
        if (this.state.responsiveFiltersOpen) {
            cb = this.props.closeFinderPanel;
        }
        this.setState({responsiveFiltersOpen: !this.state.responsiveFiltersOpen}, cb);
    };

    updatePagination = (page) => {
        this.props.setPagination(page);

        if (this.props.paginationCallback) {
            this.props.paginationCallback(page);
        }
    };

    render() {
        const {
            resource, pageSize, filters, panel, card, hasFiltering, translate, filterCount, items,
            search, setSorting, sorting, loading, page, paginatedResults, resetAllFilters, count,
            renderFooter, renderSubheader, sortingOptions, ctaCard, availableTags, skeletonType
        } = this.props;

        const title = resource && resource.title;
        const filterTitle = resource && resource.selectedFiltersTitle;
        const withSorting = resource && !resource.hideSorting;
        const withSearchbar = resource && !resource.hideSearchbar;
        const sortingValue = sorting || resource?.defaultSortingOption;

        return (
            <Block className="p-filter-container">

                <Block className={"p-filter-container__aside" + (this.state.responsiveFiltersOpen ? " open" : "")}>

                    <Block className="p-filter-container__aside-header">
                        <span className="desktop-visible"/>
                        <span onClick={this.toggleFilters} className={"svg-cross desktop-hidden flex-ignored-size"}/>

                        <span className={"text-small-bold text-uppercase p-color-black"}>{filterTitle}</span>

                        <ShowHide show={hasFiltering}>
                            <Block className="p-filter-container__aside-reset-container">
                                <span onClick={resetAllFilters} className="svg-cross-red-xs p-filter-container__aside-reset-icon desktop-visible"/>
                                <span onClick={resetAllFilters}
                                      className="p-filter-container__aside-reset-text desktop-visible">{this.props.translate("Reset")}</span>
                            </Block>
                        </ShowHide>
                        <ShowHide show={!hasFiltering}>
                            <div className="desktop-visible"/>
                        </ShowHide>
                        <span className="desktop-hidden"/>
                    </Block>

                    <Block className={"p-filter-container__aside-filtering"}>
                        {filters?.map(filter => {
                            return (
                                <Block key={filter.index}>
                                    <Filter key={"filter-" + filter.type + filter.index} filter={filter} availableTags={availableTags}
                                            availableItems={items} showSearchItemAsFilter={this.props.showSearchItemAsFilter}
                                            removeSearchItem={this.props.removeSearchItem}/>
                                </Block>
                            );
                        })}
                    </Block>

                    <Block className={"desktop-hidden p-filter-container__aside-mobile-actions"}>
                        <Link className="btn-primary"
                              onClick={this.toggleFilters} text={this.props.translate("Apply")}/>

                        <ShowHide show={hasFiltering}>
                            <Link onClick={resetAllFilters} className="btn-secondary"
                                  text={this.props.translate("Reset")} color={TextColors.red}/>
                        </ShowHide>
                    </Block>
                </Block>

                <ShowHide show={panel}>
                    <FilterPanel resource={resource} panel={panel} toggleFilters={this.toggleFilters}/>
                </ShowHide>

                <Block key="block-content" className={`p-filter-container__content${this.state.responsiveFiltersOpen ? " open" : ""}`}>

                    <Block key="block-header" className={`p-filter-container__content-header ${!withSearchbar || !search ? "no-searchbar" : ""}`}>
                        <Title heading="h1" text={title}/>

                        <ShowHide show={withSearchbar}>
                            {search}
                        </ShowHide>
                    </Block>

                    <Block key="block-body" className="p-filter-container__content-body">

                        <div className="p-filter-toggle" onClick={this.toggleFilters}>
                            <span className="p-filter-toggle__icon svg-reset-red-circle"/>
                            <span className="p-filter-toggle__text">{translate("Filter")}</span>
                        </div>


                        {withSorting && <Select key={"sorting"} className={"desktop-hidden"} onChange={setSorting} selected={sortingValue}
                                                options={sortingOptions.map(option => ({...option, label: translate(option.label)}))}/>}

                        <Block className={"p-filter-container__content-body-header"}>

                            {renderSubheader && <Block className="p-filter-container__content-body-subheader">{renderSubheader()}</Block>}

                            <Block className="p-flex p-filter-container__content-body-grid-header">
                                <ResultCounter amount={count}/>

                                <div className={"p-flex p-flex-align-center p-filter-container__content-body-grid-header-links"}>
                                    {isLoggedIn() && !resource.hideMyProductsLink && <a href={resource.myProductsPath}
                                                                                        className={"p-margin-right-sm p-link p-link__underlined text-uppercase"}>{resource.myProductsTitle}</a>}
                                    {isLoggedIn() && !resource.hideMyOrdersLink && <a href={resource.myOrdersPath}
                                                                                      className={"p-margin-right-sm p-link p-link__underlined text-uppercase"}>{resource.myOrdersTitle}</a>}
                                    {withSorting &&
                                    <Select key={"sorting"} className={"desktop-visible"} onChange={setSorting} selected={sortingValue}
                                            options={sortingOptions.map(option => ({...option, label: translate(option.label)}))}/>}
                                </div>
                            </Block>
                        </Block>

                        <WithSkeleton loading={loading} skeletonType={skeletonType}>
                            <CardList Card={card}
                                      items={paginatedResults}
                                      listSize={pageSize}
                                      emptyPlaceholder={ctaCard}
                                      ctaCard={filterCount >= 2 && !this.props.showCtaNoResultsOnly ? ctaCard : null}/>
                        </WithSkeleton>

                        <FlexRow style={FlexRow.styles.center}>
                            <Pagination activePage={page}
                                        amountToShow={4}
                                        pageClickHandler={this.updatePagination}
                                        amountOfPages={Math.floor((count + (ctaCard && !this.props.showCtaNoResultsOnly ? 1 : 0)) / pageSize) + 1}/>
                        </FlexRow>

                        {renderFooter && renderFooter()}

                    </Block>
                </Block>
            </Block>)
    }

}

const mapStateToProps = () => {
    const pageSizeSelector = createSelector(
        numberOfActiveFilterSelector,
        (state, props) => (props.resource && props.resource.maxResults) || 12,
        (activeFilters, size) => activeFilters >= 2 ? size - 1 : size
    );

    const paginator = createPaginationSelector(
        (state, ownProps) => ownProps.items,
        getPage,
        pageSizeSelector,
        (state, ownProps) => ownProps.progressivePagination
    );

    return (state, ownProps) => ({
        page: getPage(state),
        pageSize: ownProps.progressivePagination ? ownProps.resource?.maxResults : pageSizeSelector(state, ownProps),
        count: ownProps.paginationAmount ? ownProps.paginationAmount : (ownProps.items ? ownProps.items.length : 0),
        paginatedResults: paginator(state, ownProps),
        panel: getSidepanel(state),
        filters: filtersSelector(state),
        filteringValues: getTagFilters(state),
        sorting: getSorting(state),
        filterCount: numberOfActiveFilterSelector(state),
        userId: selectUserId(state),
        currencyCode: getCurrencyCode(state),
        productTileData: state.products?.productTileData,
        store: state
    });
};

const mapActionsToProps = {
    setPagination,
    setSorting,
    resetAllFilters,
    closeFinderPanel,
    fetchProductTileData
};

export const Finder = compose(withI18N, connect(mapStateToProps, mapActionsToProps))(_Finder);
